import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, RotateCcw, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader, WhatsAppButton } from "@/components/sections";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { trackEvent, EVENTS } from "@/lib/analytics";

const QUESTIONS = [
  { key: "businessType", label: "What best describes you?", options: ["Individual", "Sole Proprietor", "Company", "Professional Practice", "Partnership"] },
  { key: "turnover", label: "Approximate annual turnover", options: ["Not yet trading", "Under R1 million", "R1m – R5m", "Above R5m", "Not sure"] },
  { key: "employees", label: "How many employees do you have?", options: ["None", "1 – 5", "6 – 20", "More than 20"] },
  { key: "vat", label: "Are you VAT registered?", options: ["Yes", "No", "Not sure"] },
  { key: "payroll", label: "Do you need help with payroll?", options: ["Yes", "No"] },
  { key: "volume", label: "Roughly how many transactions per month?", options: ["Low (under 50)", "Medium (50 – 200)", "High (200+)", "Not sure"] },
  { key: "records", label: "Are your accounting records up to date?", options: ["Up to date", "Partially", "Behind", "None kept"] },
  { key: "reporting", label: "What reporting do you need?", options: ["Basic / none", "Management accounts", "Annual financial statements"] },
];

// Rule-based mapping to service AREAS. This is indicative only — no tax
// calculations or regulated advice are performed.
function recommend(a) {
  const rec = new Set();
  rec.add("Tax Services"); // tax administration is relevant to virtually everyone

  if (["Medium (50 – 200)", "High (200+)"].includes(a.volume) || ["Partially", "Behind", "None kept"].includes(a.records)) {
    rec.add("Bookkeeping");
  }
  if (a.employees && a.employees !== "None") rec.add("Payroll");
  if (a.payroll === "Yes") rec.add("Payroll");
  if (a.vat === "Yes" || a.turnover === "Above R5m" || a.turnover === "R1m – R5m") rec.add("Tax Services");
  if (["Company", "Professional Practice", "Partnership"].includes(a.businessType) || a.reporting === "Annual financial statements") {
    rec.add("Accounting & Financial Reporting");
  }
  if (a.reporting === "Management accounts" || a.turnover === "Above R5m") rec.add("Accounting & Financial Reporting");
  if (["Company", "Professional Practice", "Partnership", "Sole Proprietor"].includes(a.businessType)) rec.add("Business & Financial Support");

  return Array.from(rec);
}

const SERVICE_BLURB = {
  "Bookkeeping": "Keep your transactions, reconciliations and records organised and current.",
  "Accounting & Financial Reporting": "Management accounts and financial statements so you understand performance.",
  "Tax Services": "Returns, provisional tax and SARS administration, led by a Tax Technician Practitioner.",
  "Payroll": "Payroll processing and PAYE/UIF-related administration for your team.",
  "Business & Financial Support": "Ongoing financial organisation, insight and support as you grow.",
};

export default function NeedsAssessment() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const allAnswered = QUESTIONS.every((q) => answers[q.key]);

  const run = () => {
    const rec = recommend(answers);
    setResult(rec);
    trackEvent(EVENTS.VIEW_SERVICE, { tool: "needs-assessment", recommended: rec.join(",") });
    setTimeout(() => document.getElementById("na-result")?.scrollIntoView({ behavior: "smooth" }), 80);
  };

  const reset = () => { setAnswers({}); setResult(null); };

  return (
    <>
      <Seo
        title="Business Accounting Needs Assessment"
        description="Answer a few quick questions and get a high-level indication of the accounting services that may be relevant to your business. Indicative only."
        path="/needs-assessment"
      />
      <PageHeader
        eyebrow="Free Tool"
        title="Business Accounting Needs Assessment"
        intro="Answer a few quick questions and we'll give you a high-level indication of the accounting services that may be relevant to you. It takes under a minute."
      />

      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10" data-testid="needs-assessment-form">
            <div className="space-y-7">
              {QUESTIONS.map((q) => (
                <div key={q.key}>
                  <Label className="text-sm font-semibold text-[#18201B]">{q.label}</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {q.options.map((opt) => {
                      const selected = answers[q.key] === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswers({ ...answers, [q.key]: opt })}
                          data-testid={`na-${q.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`rounded-full border-2 px-4 py-2 text-sm font-medium transition-all ${
                            selected
                              ? "border-[#133827] bg-[#133827] text-white"
                              : "border-slate-200 bg-white text-[#526057] hover:border-[#2C5E43]/50"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button onClick={run} disabled={!allAnswered} data-testid="na-submit-btn" className="bg-[#C68B59] px-6 text-white hover:bg-[#b2794a]">
                <Sparkles className="mr-1.5 h-4 w-4" /> See My Recommendation
              </Button>
              {result && (
                <Button variant="ghost" onClick={reset} data-testid="na-reset-btn" className="text-[#526057]">
                  <RotateCcw className="mr-1.5 h-4 w-4" /> Start Over
                </Button>
              )}
            </div>
            {!allAnswered && <p className="mt-3 text-xs text-[#526057]">Please answer all questions to see your recommendation.</p>}
          </div>

          {result && (
            <div id="na-result" className="animate-fade-up mt-8 rounded-3xl border-2 border-[#2C5E43]/20 bg-[#133827] p-6 text-white sm:p-10" data-testid="needs-assessment-result">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C68B59]">Your indicative recommendation</span>
              <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">Services that may be relevant to you</h2>
              <div className="mt-6 space-y-3">
                {result.map((svc) => (
                  <div key={svc} className="flex items-start gap-3 rounded-xl bg-white/5 p-4 ring-1 ring-white/10" data-testid={`na-rec-${svc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#25D366]" />
                    <div>
                      <p className="font-display text-lg font-semibold">{svc}</p>
                      <p className="text-sm text-slate-300">{SERVICE_BLURB[svc]}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button onClick={() => { trackEvent(EVENTS.CONSULTATION_REQUEST, { from: "needs-assessment" }); navigate("/assessment"); }} data-testid="na-consult-btn" className="bg-[#C68B59] px-6 text-white hover:bg-[#b2794a]">
                  Request a Consultation <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
                <WhatsAppButton context="consultation" testId="na-whatsapp-btn" className="bg-white/5 px-6" />
              </div>

              <p className="mt-6 rounded-xl bg-black/20 p-4 text-xs leading-relaxed text-slate-300">
                Results are indicative only and should be discussed with Bonsai Financial before any
                decision is made. This tool does not provide automated regulated financial advice.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
